import type { OptionData, Promisable, LozengeProps } from '@atlaskit/user-picker';

import { ExternalUserLozengeProps } from '@confluence/external-collab-ui/entry-points/ExternalUserLozengeProps.ts';
import { ExternalGroupLozengeProps } from '@confluence/external-collab-ui/entry-points/ExternalGroupLozengeProps.ts';
import { perfMarkStart, perfMarkEnd } from '@confluence/performance/entry-points/performance.ts';

import {
	SitePermissionType,
	type UserAndGroupSearchQuery,
} from './__types__/UserAndGroupSearchQuery.ts';
import type { CustomInclusion, UserGroupItemProps } from './UserAndGroupSearchPicker.tsx';

type userGroupSearchArgs = {
	searchTerm: string;
	queryData: Promise<UserAndGroupSearchQuery>;
	customInclusion: CustomInclusion;
	setError: (error: Error) => void;
	filterResults?: (value: UserGroupItemProps) => boolean;
};

export const userGroupSearchPromise = ({
	searchTerm,
	queryData,
	customInclusion,
	setError,
	filterResults,
}: userGroupSearchArgs): Promisable<OptionData[]> => {
	const subject = 'userAndGroupSearchQuery';
	perfMarkStart({
		subject,
	});
	return queryData
		.then((data) => {
			const { users = [], groups = [] } = data.userGroupSearch ?? {};
			const userList: UserGroupItemProps[] = users.map((item) => ({
				id: `user-${item && 'accountId' in item ? item.accountId : ''}`,
				name: item?.displayName || '',
				extra: item,
				type: 'user',
				avatarUrl: item?.profilePicture ? item.profilePicture.path : '',
				lozenge:
					// ExternalUserLozengeProps type has same name as the one provided by @atlaskit/user-picker but different properties.
					// casting for now to avoid TS error, no change to original properties
					item?.permissionType === SitePermissionType.EXTERNAL
						? (ExternalUserLozengeProps as LozengeProps)
						: null,
			}));
			const groupList: UserGroupItemProps[] = groups.map((item) => ({
				id: `group-${item?.id}`,
				name: item?.name || '',
				type: 'group',
				extra: item,
				lozenge:
					// ExternalUserLozengeProps type has same name as the one provided by @atlaskit/user-picker but different properties.
					// casting for now to avoid TS error, no change to original properties
					item?.permissionType === SitePermissionType.EXTERNAL
						? (ExternalGroupLozengeProps as LozengeProps)
						: null,
			}));
			const customInclusionList: UserGroupItemProps[] = [];
			const cleanedSearchTerm = searchTerm.replace(/\s/g, '').toLowerCase();
			customInclusion.forEach((inclusionObj) => {
				Object.keys(inclusionObj).forEach((key) => {
					if (key.toLowerCase().includes(cleanedSearchTerm)) {
						customInclusionList.push(inclusionObj[key]);
					}
				});
			});

			const usersGroupsAndAccessClasses = [...customInclusionList, ...userList, ...groupList];
			perfMarkEnd({
				subject,
			});
			return filterResults
				? usersGroupsAndAccessClasses.filter(filterResults)
				: usersGroupsAndAccessClasses;
		})
		.catch((error) => {
			setError(error);
			return [];
		});
};
